/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

/**
 * Helper function to transform bundle options data to be ready for a CartItem SDK component
 */
export const transformBundleOptions = (data: any) => {
  const bundle = data?.map((option: any) => ({
    label: option.label,
    value: option.values.map((value: any) => value.label).join(', '),
  }));

  const bundleOptions: { [key: string]: any } = {};

  bundle?.forEach((option: any) => {
    bundleOptions[option.label] = option.value;
  });

  return Object.keys(bundleOptions).length > 0 ? bundleOptions : null;
};

/**
 * Helper function to transform configurable options data to be ready for a CartItem SDK component
 */
export const transformConfigurableOptions = (data: any) => {
  const configurable = data?.map((option: any) => ({
    label: option.optionLabel,
    value: option.valueLabel,
  }));

  const configurableOptions: { [key: string]: any } = {};

  configurable?.forEach((option: any) => {
    configurableOptions[option.label] = option.value;
  });

  return Object.keys(configurableOptions).length > 0
    ? configurableOptions
    : null;
};

/**
 * Helper function to transform customizable options data to be ready for a CartItem SDK component
 */
export const transformCustomizableOptions = (data: any) => {
  const transformedOptions = data?.map((option: any) => ({
    label: option.label,
    type: option.type,
    values: option.values.map((value: any) => ({
      label: value.label,
      value: value.value,
    })),
  }));

  const result: { [key: string]: any } = {};

  transformedOptions?.forEach((option: any) => {
    switch (option.type) {
      case 'field':
      case 'area':
      case 'date_time':
        result[option.label] = option.values[0].value;
        break;
      case 'radio':
      case 'drop_down':
        result[option.label] = option.values[0].label;
        break;
      case 'multiple':
      case 'checkbox':
        result[option.label] = (option.values as Array<any>).reduce(
          (previous, current) => {
            if (previous) return `${previous}, ${current.label}`;
            return current.label;
          },
          ''
        );
        break;
      case 'file': {
        const parser = new DOMParser();
        const fileAnchorElementHtml = option.values[0].value;
        const doc = parser.parseFromString(fileAnchorElementHtml, 'text/html');
        const anchorText = doc.querySelector('a')?.textContent || '';
        result[option.label] = anchorText;
        break;
      }
    }
  });

  return result;
};
